import { useTranslation } from 'react-i18next'

import type { ResumoSimulacao } from '@/features/proposta/entities/ResumoProposta/resumo-simulacoes-proposta'
import type { TPropostaLocationState } from '@/features/proposta/interfaces/proposta.type'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { FluxoPagamentoSheet } from '@/features/simulacao/components/fluxo-pagamento'
import { useFluxoPagamento } from '@/features/simulacao/hooks/use-fluxo-pagamento'
import { InfoItem } from '@/pages/proposta/resumo-proposta/components/info-item'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui'
import { useTypedLocation } from '@/shared/hooks/use-typed-location'
import { cn } from '@/shared/lib'

type TCardPropostaCompraProps = {
  resumo: ResumoSimulacao
  vagaIds: TPropostaSchemaInput['informacoesIniciais']['vagaIds']
  plantaDescricao: string
  onEdit: (step: number) => void
}

type TListaValoresProps = {
  rotulo: string
  valores: string[]
  bordaDireita?: boolean
}

function ListaValores({ rotulo, valores, bordaDireita }: Readonly<TListaValoresProps>) {
  return (
    <div className={['space-y-1', bordaDireita && 'border-r border-neutral-100'].filter(Boolean).join(' ')}>
      <p className="text-sm font-medium text-neutral-600">{rotulo}</p>
      {valores.map((v) => (
        <p key={v} className="text-base text-neutral-800">
          {v}
        </p>
      ))}
    </div>
  )
}

const STEP_INFORMACOES_INICIAIS = 0

export function CardPropostaCompra({ resumo, vagaIds, plantaDescricao, onEdit }: Readonly<TCardPropostaCompraProps>) {
  const { t } = useTranslation('proposta')
  const { state } = useTypedLocation<TPropostaLocationState>()
  const { data } = useFluxoPagamento(state.simulacoesIds)

  const vagas = vagaIds.some((v) => !v.substituiVagaPadrao) ? vagaIds.map((v) => v.descricao).join(', ') : '-'
  const isLote = resumo.tipoDeProduto === 'Lote'
  return (
    <Card className="shadow-none">
      <CardHeader className="bg-neutral-200 py-3">
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
          <CardTitle className="text-base font-normal text-neutral-900">
            {t('resumo_proposta.proposta_compra.titulo')}
          </CardTitle>
          <div className="flex flex-col md:block">
            <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={data?.length ? data : []}>
              <Button
                variant="ghost"
                size="sm"
                className="text-primary border-primary mb-3 w-full border-1 hover:bg-neutral-200 md:mb-0 md:w-fit md:border-none"
              >
                <Icon name="eye-open" className="mr-1 h-4 w-4" />
                {t('resumo_proposta.proposta_compra.fluxo_pagamento')}
              </Button>
            </FluxoPagamentoSheet>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary border-primary border-1 hover:bg-neutral-200 md:border-none"
              onClick={() => onEdit(STEP_INFORMACOES_INICIAIS)}
            >
              <Icon name="pencil" className="mr-1 h-4 w-4" />
              {t('resumo_proposta.editar')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-6 py-4">
        <div className={cn('grid grid-cols-1 gap-4 py-1 md:grid-cols-2', isLote ? 'border-b border-neutral-100' : '')}>
          <InfoItem
            data-testid="info-item-empreendimento"
            rotulo={t('resumo_proposta.proposta_compra.label.empreendimento')}
            valor={resumo.nomeEmpreendimento}
            bordaDireita
          />
          <InfoItem
            data-testid="info-item-bloco"
            rotulo={resumo.labelBloco}
            valor={resumo.blocoEmpreendimento}
            bordaDireita
          />
          <Render.If condition={isLote}>
            {() => (
              <InfoItem
                data-testid="info-item-produto"
                rotulo={resumo.labelProduto}
                valor={resumo.codigoProdutoPrincipal}
              />
            )}
          </Render.If>
        </div>
        <Render.If condition={!isLote}>
          {() => (
            <>
              <div className="grid grid-cols-1 gap-4 py-1 md:grid-cols-2">
                <InfoItem
                  data-testid="info-item-produto"
                  rotulo={resumo.labelProduto}
                  valor={resumo.codigoProdutoPrincipal}
                  bordaDireita
                  className="border-gray-100"
                />
                <InfoItem
                  data-testid="info-item-vaga"
                  rotulo={t('resumo_proposta.proposta_compra.label.vaga_padrao')}
                  valor={vagas}
                />
              </div>
              <InfoItem
                data-testid="info-item-acessorios"
                rotulo={t('resumo_proposta.proposta_compra.label.acessorio')}
                valor={resumo.codigosAcessorios}
                bordaInferior
                className="pt-1 pb-2"
              />
              <div className="grid grid-cols-1 gap-4 border-b border-neutral-100 py-2 md:grid-cols-2">
                <InfoItem
                  data-testid="info-item-marcacao-alvenaria"
                  rotulo={t('resumo_proposta.proposta_compra.label.marcacao_alvenaria')}
                  valor={resumo.dataMarcacaoAlvenaria}
                  bordaDireita
                />
                <InfoItem
                  data-testid="info-item-planta"
                  rotulo={t('resumo_proposta.proposta_compra.label.planta')}
                  valor={plantaDescricao || '-'}
                />
              </div>
            </>
          )}
        </Render.If>
        <InfoItem
          data-testid="info-item-valor-proposta"
          rotulo={t('resumo_proposta.proposta_compra.label.valor_total_proposta')}
          valor={resumo.valorProposta}
          bordaInferior
          className="py-2"
        />
        <div className="grid grid-cols-1 gap-4 border-b border-neutral-100 py-2 md:grid-cols-2">
          <InfoItem
            data-testid="info-item-valor-poupanca"
            rotulo={t('resumo_proposta.proposta_compra.label.poupanca')}
            valor={resumo.valorPoupanca}
            bordaDireita
          />
          <InfoItem
            data-testid="info-item-saldo"
            rotulo={t('resumo_proposta.proposta_compra.label.saldo')}
            valor={resumo.valorSaldo}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 border-b border-neutral-100 py-2 md:grid-cols-2">
          <ListaValores
            data-testid="info-item-entrada"
            rotulo={t('resumo_proposta.proposta_compra.label.entrada')}
            valores={resumo.entrada}
            bordaDireita
          />
          <ListaValores rotulo={t('resumo_proposta.proposta_compra.label.mensais')} valores={resumo.mensais} />
        </div>
        <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2">
          <ListaValores
            data-testid="info-item-intermediarias"
            rotulo={t('resumo_proposta.proposta_compra.label.intermediarias')}
            valores={resumo.intermediarias}
            bordaDireita
          />
          <ListaValores
            data-testid="info-item-pos-conclusao"
            rotulo={t('resumo_proposta.proposta_compra.label.pos_conclusao')}
            valores={resumo.posConclusao}
          />
        </div>
      </CardContent>
    </Card>
  )
}
